import axios from 'axios';
import { SUPPORTED_CONFIG_FILE_TYPES } from './constants';
import { notify } from './utils';

export const getCompany = (companyId, config) =>
  axios
    .get(`/companies/${companyId}`, config)
    .then(({ data }) => data)
    .catch((err) => {
      console.error(err, 'Could not retrieve company details');
      return {};
    });

export const getCompanyPrimaryUser = (companyId, config) =>
  axios
    .get(`/companies/${companyId}/primary-user`, config)
    .then(({ data }) => data)
    .catch((err) => {
      console.error(err, 'Could not retrieve company primary user');
      throw err; // we want to catch
    });

export const getCountries = () =>
  axios
    .get('/info/countries')
    .then(({ data }) => data)
    .catch((error) => {
      console.error(error, 'Could not retrieve countries');
    });

export const exportAuditLogs = (params, tenantId) => {
  const url = tenantId
    ? `/support/tenants/${tenantId}/audit_log/export`
    : '/admin/audit_log/export';
  return axios
    .get(url, {
      headers: {
        Accept: `text/csv`,
      },
      params,
    })
    .then(({ data }) => data)
    .catch((error) => {
      notify.failed(
        error,
        'Encountered an issue while exporting the audit log csv file:'
      );
    });
};

export const getCompanyConfig = (companyId, fileType) => {
  const isValidFileType = Object.values(SUPPORTED_CONFIG_FILE_TYPES).includes(
    fileType
  );
  if (!isValidFileType) {
    throw new TypeError(
      `${fileType} is not supported for exporting company config file!`
    );
  }

  return axios
    .get(`/config/${companyId}`, {
      headers: {
        Accept: `application/${fileType}`,
      },
    })
    .then(({ data }) => data)
    .catch((err) => {
      notify.failed(
        err,
        'Encountered an issue while exporting the configuration file:'
      );
    });
};

export const getDeltaFromCompanyConfig = (companyId, config, fileType) => {
  return axios
    .post(`/config/${companyId}/delta`, config, {
      headers: {
        Accept: 'application/json',
        'content-type': `application/${fileType}`,
      },
    })
    .then(({ data }) => data)
    .catch((err) => {
      notify.failed(
        err,
        'Encountered an issue while requesting configuration delta:'
      );
    });
};

export const applyCompanyConfigDeltaExecutablePlan = (
  companyId,
  executablePlan,
  label
) => {
  return axios
    .post(`/config/${companyId}/delta/apply`, {
      label,
      executablePlan,
    })
    .then(({ data }) => data)
    .catch((err) => {
      notify.failed(
        err,
        'Encountered an issue while requesting to apply the company configuration delta executable plan:'
      );
    });
};

export const getEdifierTaskReport = (taskId, isCreateTask = true) => {
  const url = `/config/${isCreateTask ? 'create-report' : 'report'}/${taskId}`;
  return axios
    .get(url)
    .then(({ data }) => data)
    .catch((err) => {
      notify.failed(
        err,
        'Encountered an issue while fetching the edifier task report'
      );
    });
};

export const getEdifierCreatedTasks = (params = {}) => {
  return axios
    .get(`/config/create-tasks`, { params })
    .then(({ data }) => data)
    .catch((err) => {
      notify.failed(
        err,
        'Encountered an issue while fetching the edifier create tasks list'
      );
    });
};

export const getEdifierTenantTasks = (tenantId, params = {}) => {
  return axios
    .get(`/config/${tenantId}/tasks`, { params })
    .then(({ data }) => data)
    .catch((err) => {
      notify.failed(
        err,
        'Encountered an issue while fetching the edifier tenant tasks list'
      );
    });
};

export const deleteAllImpersonatorMemberships = (user, allMemberships) => {
  return axios
    .delete(`/admin/user/remove_impersonator_memberships/${user.id}`, {
      params: { all_memberships: allMemberships },
      data: user,
    })
    .then(
      () => notify.updated('User impersonator memberships'),
      (error) =>
        notify.failed(error, 'Could not update impersonator memberships')
    );
};

export const deleteAllImpersonatorMembershipsCurrentUser = (allMemberships) => {
  return axios
    .delete(`/support/user/remove_impersonator_memberships`, {
      params: { all_memberships: allMemberships },
    })
    .then(
      () => notify.updated('Your impersonator memberships'),
      (error) =>
        notify.failed(error, 'Could not update impersonator memberships')
    );
};

export const convertXlsxConfigToYaml = (content) => {
  return axios.put(`/config/convert`, content, {
    headers: {
      'Content-Type': 'application/octet-stream',
    },
  });
};
